import React from 'react';
import Content from '../../../layout/content/Content';
import Head from '../../../layout/head/Head';
import { Block, BlockBetween, BlockHead, BlockHeadContent, BlockTitle } from '../../../components/Component';
import { Card, CardTitle, Col, Row } from 'reactstrap';
import { currencyFormatter } from '../../explorer/utils';
import Skeleton from 'react-loading-skeleton';
import CardInner from '../../../components/cards/CardInner';
import CardTitleGroup from '../../../components/cards/CardTitleGroup';
import DealProjectionsView from './DealProjectionsView';

const renderPercentage = (value, label) => {
  const isNegative = value < 0;
  const isPositive = value > 0;

  if (isNegative) {
    return (
      <span>
        <span className="text-danger">{Math.abs(Math.round(value))}% lower</span> than {label}
      </span>
    );
  }

  if (isPositive) {
    return (
      <span>
        <span className="text-success">{Math.abs(Math.round(value))}% higher</span> than {label}
      </span>
    );
  }

  return <span className="text-muted">No available data for {label}</span>;
};

const RevenueView = ({ data, includeEoi, setIncludeEoi }) => {
  const {
    earnings,
    earningsThisYear,
    netReceivedThisMonth,
    netReceivedThisYear,
    netReceivedYearlyComparison,
    netReceivedMonthlyComparison,
    netReceivedTargetComparison,
    dealProjections,
  } = data;

  const grossCommissions = earningsThisYear?.data?.earned?.totalEarned ?? 0;

  return (
    <React.Fragment>
      <Head title="Revenue"></Head>
      <Content>
        <BlockHead>
          <BlockBetween>
            <BlockHeadContent>
              <BlockTitle className={''}>Revenue</BlockTitle>
            </BlockHeadContent>
          </BlockBetween>
        </BlockHead>
        <Block>
          <div>
            <Row>
              <Col sm={6}>
                <Card style={{ borderRadius: '14px' }}>
                  <CardInner className="h-100 d-flex flex-column" style={{ gap: '.5rem' }}>
                    <CardTitleGroup>
                      <CardTitle>
                        <h4 className="title">Net received this month</h4>
                      </CardTitle>
                    </CardTitleGroup>
                    <div className="data-group">
                      <div className="fs-4">
                        {netReceivedThisMonth.isLoading ? (
                          <Skeleton style={{ width: '200px', height: '20px' }} />
                        ) : (
                          currencyFormatter({ isDecimal: true }).format(
                            netReceivedThisMonth?.data?.received?.netReceived ?? 0
                          )
                        )}
                      </div>
                    </div>
                  </CardInner>
                </Card>

                <Card className="mt-2" style={{ borderRadius: '14px' }}>
                  <CardInner className="h-100 d-flex flex-column" style={{ gap: '.5rem' }}>
                    <CardTitleGroup>
                      <CardTitle>
                        <h4 className="title">Net earnings yet to be received (all time)</h4>
                      </CardTitle>
                    </CardTitleGroup>
                    <div className="data">
                      <div className="data-group">
                        <div className="fs-4">
                          {earnings.isLoading ? (
                            <Skeleton style={{ width: '200px', height: '20px' }} />
                          ) : (
                            currencyFormatter({ isDecimal: true }).format(
                              (earnings?.data?.balance?.balanceLeft ?? 0) / 2
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </CardInner>
                </Card>

                <Card className="mt-2" style={{ borderRadius: '14px' }}>
                  <CardInner className="h-100 d-flex flex-column" style={{ gap: '.5rem' }}>
                    <CardTitleGroup>
                      <CardTitle>
                        <h4 className="title">Net received this year</h4>
                      </CardTitle>
                    </CardTitleGroup>
                    <div className="data-group">
                      <div className="fs-4">
                        {netReceivedThisYear.isLoading ? (
                          <Skeleton style={{ width: '200px', height: '20px' }} />
                        ) : (
                          currencyFormatter({ isDecimal: true }).format(
                            netReceivedThisYear?.data?.received?.netReceived ?? 0
                          )
                        )}
                      </div>
                    </div>
                  </CardInner>
                </Card>
              </Col>
              <Col sm={6} className="">
                <div className="h-100 d-flex flex-column" style={{ gap: '.5rem' }}>
                  <div className="bg-white p-4" style={{ borderRadius: '14px' }}>
                    <h4 className="title">Net Received</h4>
                    <div className="data-group">
                      <div className="fs-5">
                        {netReceivedYearlyComparison.isLoading ? (
                          <Skeleton style={{ width: '400px', height: '20px' }} />
                        ) : (
                          <span>
                            {renderPercentage(
                              netReceivedYearlyComparison?.data?.percentageDifference,
                              'this time last year'
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4" style={{ borderRadius: '14px' }}>
                    <h4 className="title">Net Received</h4>
                    <div className="data-group">
                      <div className="fs-5">
                        {netReceivedMonthlyComparison.isLoading ? (
                          <Skeleton style={{ width: '400px', height: '20px' }} />
                        ) : (
                          <span>
                            {renderPercentage(
                              netReceivedMonthlyComparison?.data?.percentageDifference,
                              'this time last month'
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4" style={{ borderRadius: '14px' }}>
                    <h4 className="title">Net Received</h4>
                    <div className="data-group">
                      <div className="fs-5">
                        {netReceivedTargetComparison.isLoading ? (
                          <Skeleton style={{ width: '400px', height: '20px' }} />
                        ) : (
                          <span>
                            {renderPercentage(
                              netReceivedTargetComparison?.data?.percentageDifference,
                              "this month's target"
                            )}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="bg-white p-4" style={{ borderRadius: '14px' }}>
                    <h4 className="title">All commissions (gross) earned this year to date</h4>
                    <div className="data-group">
                      <div className="fs-5">
                        {earningsThisYear.isLoading ? (
                          <Skeleton style={{ width: '200px', height: '20px' }} />
                        ) : (
                          currencyFormatter({ isDecimal: true }).format(grossCommissions)
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
        </Block>
        <Block>
          <DealProjectionsView 
            data={dealProjections?.data} 
            isError={dealProjections?.isError} 
            isLoading={dealProjections?.isLoading} 
            refetch={dealProjections?.refetch}
            includeEoi={includeEoi}
            setIncludeEoi={setIncludeEoi}
          />
        </Block>
      </Content>
    </React.Fragment>
  );
};

export default RevenueView;
